import React from 'react';
import { HiddenSectionsProvider } from '@/context/HiddenSectionsContext';
import { EmptyVehicleSectionDisclaimer } from '@/reports/shared/ReportSection/EmptyVehicleSectionDisclaimer/EmptyVehicleSectionDisclaimer';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import {
  AutomobileAccidents,
  AutomobileAuctions,
  AutomobileSalvages,
  AutomobileReportedDamages,
  AutomobileRepairQuotes,
} from '@/reports/types/vehicles';
import { pluralizeString } from '@/utils/pluralizeString';
import { HideableSection } from '../HideableSection/HideableSection';
import { VehicleAccidents } from '../VehicleAccidents/VehicleAccidents';
import { VehicleDamage } from '../VehicleDamage/VehicleDamage';
import { VehicleRepairQuotes } from '../VehicleRepairQuotes/VehicleRepairQuotes';
import { VehicleSalvages } from '../VehicleSalvages/VehicleSalvages';

interface VehicleDamageHistoryProps {
  vehicleAccidentsList: AutomobileAccidents;
  vehicleAuctionsDamageList: AutomobileAuctions | AutomobileReportedDamages;
  vehicleSalvagesList: AutomobileSalvages;
  vehicleRepairQuotesList: AutomobileRepairQuotes;
  nmvtisSuccess: boolean | undefined;
  reportUpgraded: boolean;
  handleFreeUpgrade: () => void;
}

export function VehicleDamageHistory({
  vehicleAccidentsList,
  vehicleAuctionsDamageList,
  vehicleSalvagesList,
  vehicleRepairQuotesList,
  nmvtisSuccess,
  reportUpgraded,
  handleFreeUpgrade,
}: VehicleDamageHistoryProps) {
  const itemsCount =
    vehicleAccidentsList?.length +
    vehicleAuctionsDamageList?.length +
    vehicleSalvagesList?.length +
    vehicleRepairQuotesList?.length;
  const title = itemsCount
    ? `${itemsCount} Damage ${pluralizeString('Record', itemsCount)} Reported`
    : 'Damage History';

  return (
    <ReportSection
      supertitleText="Damage History"
      showTopBar
      title={title}
      hasContent
      sectionId="vehicle-damage-history"
      data-testid="vehicle-damage-history-report-section"
      customEmptyText={<EmptyVehicleSectionDisclaimer title="damage history" />}
    >
      <HiddenSectionsProvider reportType="vehicle_report">
        <>
          <HideableSection
            nameToDisplay="Accidents Reported"
            nameToRecord="accidents_reported"
            id="vehicle-damage-history"
            isNested
          >
            <VehicleAccidents vehicleAccidentsList={vehicleAccidentsList} />
          </HideableSection>
          <HideableSection
            nameToDisplay="Damage Reported"
            nameToRecord="damage_reported"
            id="vehicle-damage-history"
            isNested
          >
            <VehicleDamage
              vehicleAuctionsDamageList={vehicleAuctionsDamageList}
            />
          </HideableSection>
          <HideableSection
            nameToDisplay="Repair Quotes"
            nameToRecord="repair_quotes"
            id="vehicle-damage-history"
            isNested
          >
            <VehicleRepairQuotes
              vehicleRepairQuotesList={vehicleRepairQuotesList}
            />
          </HideableSection>
          <HideableSection
            nameToDisplay="Salvage Records"
            nameToRecord="salvage_records"
            id="vehicle-damage-history"
            isNested
          >
            <VehicleSalvages
              vehicleSalvagesList={vehicleSalvagesList}
              nmvtisSuccess={nmvtisSuccess}
              reportUpgraded={reportUpgraded}
              handleFreeUpgrade={handleFreeUpgrade}
            />
          </HideableSection>
        </>
      </HiddenSectionsProvider>
    </ReportSection>
  );
}
